// ---------------------------------------------------------------------------
// Job Postings
// ---------------------------------------------------------------------------
// To add a new job posting, add an entry to JOB_POSTINGS below.
//
// content.sections defines the body of the posting after the auto-generated
// intro. Each section has a title and either paragraphs, bullets, or both.
// Use **text** inside bullet strings to render bold inline text.
//
// Example:
// {
//   slug: "my-new-role",
//   title: "My New Role",
//   department: "Engineering",
//   location: "Remote",
//   type: "Full-time",
//   content: {
//     sections: [
//       {
//         title: "Job Overview",
//         paragraphs: ["Description of the role..."],
//       },
//       {
//         title: "Required Qualifications",
//         bullets: ["**3+ years** of experience with...", "Proficiency in **TypeScript**"],
//       },
//       {
//         title: "Preferred Qualifications",
//         subtitle: "Candidates with these qualifications will be given special consideration.",
//         bullets: ["Experience with **Electron**"],
//       },
//     ],
//   },
// },
// ---------------------------------------------------------------------------

export interface JobPostingSection {
  title: string;
  /** Optional subtitle rendered below the heading. */
  subtitle?: string;
  /** Plain text paragraphs rendered in order. */
  paragraphs?: string[];
  /** Bullet list items. Wrap text in **double asterisks** to render it bold. */
  bullets?: string[];
}

export interface JobPostingContent {
  sections: JobPostingSection[];
}

export interface JobPosting {
  /** URL-friendly identifier, e.g. "senior-engineer" → /careers/senior-engineer */
  slug: string;
  /** Display title shown on the listings page and the posting header. */
  title: string;
  /** Team or department name, e.g. "Engineering", "Marketing". */
  department: string;
  /** Work location, e.g. "Remote", "Austin, TX". */
  location: string;
  /** Employment type, e.g. "Full-time", "Part-time", "Contract". */
  type: string;
  content: JobPostingContent;
}

export const JOB_POSTINGS: JobPosting[] = [
  {
    slug: "senior-engineer",
    title: "Senior Software Engineer",
    department: "Engineering",
    location: "Remote",
    type: "Full-time",
    content: {
      sections: [
        {
          title: "Job Overview",
          paragraphs: [
            "As a Senior Software Engineer at U.GG, you'll play a key role in building, scaling, and refining products used by millions of players every month. You'll work across our web and desktop platforms to deliver reliable, high-performance features while helping set technical direction and best practices for the team. Ideal candidates should be comfortable owning features end-to-end across frontend and backend systems, ranging from frontend website changes to backend platform and service ownership.",
            "This role blends hands-on engineering with technical leadership. You'll take ownership of complex initiatives end-to-end, collaborate closely with product and design, and help mentor other engineers. We're looking for someone who is comfortable operating across multiple product environments, cares deeply about code quality and user experience, and is excited to build tools for players at scale.",
            "You'll have a meaningful influence on architecture decisions, performance improvements, and the ongoing evolution of our tech stack as U.GG continues to grow within League of Legends and expands into new games and opportunities.",
          ],
        },
        {
          title: "Required Qualifications",
          bullets: [
            "**5+ years** of professional software engineering experience, including **2+ years** in a senior or technical lead capacity.",
            "Proven ability to **own and deliver complex technical initiatives**, supporting live services with **hundreds of thousands of daily active users**.",
            "Strong proficiency in **JavaScript/TypeScript**, **React**, **Tailwind**, and **Node.js**.",
            "Hands-on experience with **AWS**, **database management**, and **GraphQL**.",
            "Experience integrating **analytics**, **ad tech**, and **authentication** systems.",
            "Solid understanding of **desktop performance optimization**, **memory profiling**, and **multi-platform builds**.",
            "Familiarity with **CI/CD pipelines**, **automated testing**, and **release management**.",
            "Excellent **communication and collaboration** skills, with the ability to guide and align **technical direction across teams**.",
            "Passion for **gaming**, **esports**, and building tools that improve the **player experience**.",
          ],
        },
        {
          title: "Preferred Qualifications",
          subtitle: "Candidates with these qualifications will be given special consideration.",
          bullets: [
            "Experience with **Electron**, **Elixir**, or **native overlays** in progressive web applications.",
            "Background integrating **ad tech** in hybrid or desktop applications, ensuring optimal performance and compliance with platform and publisher policies.",
            "Experience leveraging **AI/ML-powered APIs or models** to enhance web or app functionality.",
            "A passion for **League of Legends**, **World of Warcraft**, **Diablo**, or **Deadlock**, and familiarity with ecosystems from **Riot Games**, **Blizzard**, or **Valve**.",
          ],
        },
      ],
    },
  },
];
